import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalContent, ModalFooter } from '@heroui/react';
import useIsMobile from '@/hooks/useIsMobile';
import { btnGhost, btnPrimary } from '@/components/ui/design';

type DialogOptions = {
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
};

type DialogApi = {
    confirm: (options: DialogOptions) => Promise<boolean>;
    alert: (options: DialogOptions | string) => Promise<void>;
};

type OpenDialog = DialogOptions & { kind: 'confirm' | 'alert' };

const DialogContext = createContext<DialogApi | null>(null);

export const useDialogs = (): DialogApi => {
    const api = useContext(DialogContext);
    if (!api) throw new Error('useDialogs must be used inside DialogProvider');
    return api;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isMobile = useIsMobile();
    const [dialog, setDialog] = useState<OpenDialog | null>(null);
    const resolver = useRef<((value: boolean) => void) | null>(null);

    const open = useCallback((next: OpenDialog) => new Promise<boolean>(resolve => {
        resolver.current?.(false);
        resolver.current = resolve;
        setDialog(next);
    }), []);

    const close = useCallback((value: boolean) => {
        resolver.current?.(value);
        resolver.current = null;
        setDialog(null);
    }, []);

    const api = useMemo<DialogApi>(() => ({
        confirm: options => open({ ...options, kind: 'confirm' }),
        alert: async options => {
            await open({ ...(typeof options === 'string' ? { message: options } : options), kind: 'alert' });
        },
    }), [open]);

    return (
        <DialogContext.Provider value={api}>
            {children}
            <Modal
                isOpen={!!dialog}
                onClose={() => close(false)}
                size={isMobile ? 'full' : 'md'}
                classNames={{
                    base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                    wrapper: 'p-0 md:p-4 z-[100000]',
                    body: 'px-6',
                    footer: 'px-6 pb-6',
                    closeButton: 'right-4 top-4 rounded-full hover:bg-grey99 dark:hover:bg-white/10',
                    backdrop: 'bg-black/50',
                }}
            >
                <ModalContent>
                    <ModalBody className="pt-8 pb-4 flex flex-col justify-center md:justify-start">
                        {dialog?.title && <h3 className="mb-3 text-2xl font-bold leading-tight tracking-[-0.03em] text-grey10 dark:text-white">{dialog.title}</h3>}
                        <p className="text-base leading-relaxed text-grey40 dark:text-nightMuted">{dialog?.message}</p>
                    </ModalBody>
                    <ModalFooter className="flex flex-col sm:flex-row gap-3">
                        {dialog?.kind === 'confirm' && (
                            <Button onPress={() => close(false)} variant="bordered" className={`${btnGhost} w-full sm:w-auto border-0 bg-transparent`}>
                                {dialog.cancelLabel ?? 'Cancel'}
                            </Button>
                        )}
                        <Button
                            onPress={() => close(true)}
                            className={dialog?.destructive
                                ? 'inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-error50 px-6 text-sm font-semibold text-white transition-colors hover:bg-error40'
                                : `${btnPrimary} w-full sm:w-auto`}
                        >
                            {dialog?.confirmLabel ?? (dialog?.kind === 'alert' ? 'OK' : 'Continue')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </DialogContext.Provider>
    );
};
